import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '@shared/auth/msal.config';

type Props = {
  readonly children: ReactNode;
};

/**
 * Set by main.tsx when handleRedirectPromise() fails mid-exchange.
 * Most commonly caused by a browser extension blocking login.microsoftonline.com.
 */
const EXCHANGE_FAILED_KEY = 'dcl.msal.exchange-failed';

export function RequireAuth({ children }: Props): JSX.Element | null {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const [redirectFailed, setRedirectFailed] = useState(false);
  // True when handleRedirectPromise threw (set by main.tsx before React mounts).
  const [exchangeFailed, setExchangeFailed] = useState(() => {
    try { return !!sessionStorage.getItem(EXCHANGE_FAILED_KEY); }
    catch { return false; }
  });
  // Bumped whenever the redirect lock denies us — used to retry after the lock expires.
  const [retryTick, setRetryTick] = useState(0);

  const returnTo = `${location.pathname}${location.search}${location.hash}`;

  useEffect((): (() => void) | void => {
    if (isAuthenticated || inProgress !== InteractionStatus.None) {
      return;
    }
    // If the URL still contains an MSAL response fragment (`#code=...` /
    // `#error=...`), MsalProvider's `handleRedirectPromise()` is about to
    // run / has just run. Do NOT fire another loginRedirect now — it would
    // overlap with MSAL's own internal `window.location.replace` to the
    // login-start-page and create a redirect storm.
    if (typeof window !== 'undefined') {
      const frag = window.location.hash.slice(1);
      const search = window.location.search.slice(1);
      if (
        frag.includes('code=') || frag.includes('error=') ||
        search.includes('code=') || search.includes('error=')
      ) {
        return;
      }
    }
    // Hard rate-limit any interactive redirect — see main.tsx for the rationale.
    const guard = (window as unknown as { __dclRedirectGuard?: { isLocked: () => boolean; mark: () => void } })
      .__dclRedirectGuard;
    if (guard?.isLocked()) {
      // Lock held — schedule a single retry after it expires so we don't strand
      // the user on a blank page.
      const handle = window.setTimeout(() => setRetryTick((n) => n + 1), 2_100);
      return () => window.clearTimeout(handle);
    }
    guard?.mark();
    instance.loginRedirect({
      scopes: [...loginRequest.scopes],
      state: returnTo,
      // No `prompt` here — let Microsoft use the existing Entra SSO session when available.
      // AADSTS50140 (KMSI interrupt) is handled in main.tsx via ssoSilent retry.
    }).catch((_err: unknown) => {
      // Most errors here are transient (interaction_in_progress race) or
      // caused by missing/invalid MSAL configuration (.env not set up).
      // Show a helpful message instead of a blank screen.
      setRedirectFailed(true);
    });
  }, [instance, inProgress, isAuthenticated, returnTo, retryTick]);

  // Token exchange was blocked (most likely by a browser extension intercepting
  // the POST to login.microsoftonline.com). Show an actionable error so the user
  // isn't left staring at a blank page.
  if (exchangeFailed && !isAuthenticated) {
    return (
      <div style={{ padding: 32, fontFamily: 'sans-serif', color: '#fff', maxWidth: 600 }}>
        <strong>Sign-in failed — Microsoft login was blocked</strong>
        <p style={{ marginTop: 12, opacity: 0.85, lineHeight: 1.6 }}>
          A browser extension blocked the request to{' '}
          <code>login.microsoftonline.com</code> before your token could be issued.
          This is the most common reason sign-in works in Incognito (extensions
          disabled) but not in a normal browser window.
        </p>
        <p style={{ marginTop: 8, opacity: 0.7, lineHeight: 1.6 }}>
          <strong>To fix:</strong> open your browser&apos;s extensions panel, find an
          ad-blocker or privacy extension (e.g.&nbsp;uBlock&nbsp;Origin, Privacy&nbsp;Badger,
          Ghostery, AdGuard) and whitelist&nbsp;
          <code>login.microsoftonline.com</code>, then click the button below.
        </p>
        <button
          type="button"
          onClick={() => {
            try { sessionStorage.removeItem(EXCHANGE_FAILED_KEY); } catch { /* noop */ }
            setExchangeFailed(false);
            setRetryTick((n) => n + 1);
          }}
          style={{
            marginTop: 16, padding: '8px 20px', background: '#0078d4',
            color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Try sign-in again
        </button>
      </div>
    );
  }

  if (redirectFailed) {
    return (
      <div style={{ padding: 32, fontFamily: 'sans-serif', color: '#fff' }}>
        <strong>Sign-in could not be started.</strong>
        <p style={{ marginTop: 8, opacity: 0.7 }}>
          Check that <code>.env</code> contains valid{' '}
          <code>VITE_MSAL_CLIENT_ID</code>, <code>VITE_MSAL_TENANT_ID</code> and{' '}
          <code>VITE_MSAL_REDIRECT_URI</code> values, then restart the dev server.
        </p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }
  return <>{children}</>;
}
